import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import {
  BoltIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ArrowRightIcon,
  LightBulbIcon,
  AdjustmentsHorizontalIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

export default function FlowsGuidePage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to={`${basePath}/help`} className="text-gray-500 hover:text-gray-700">
                Help Center
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li className="text-gray-900">Flows Guide</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BoltIcon className="h-8 w-8 text-blue-600 mr-3" />
            Master Your Flows Page
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            Discover, analyze, and manage all your Salesforce flows with powerful search, filtering, and performance grading capabilities.
          </p>
        </div>

        {/* Overview Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">What You Can Do</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <p className="text-gray-700 mb-4">
              The Flows page is your command center for managing all Salesforce automation. It provides comprehensive visibility into flow health, performance grades, and enables quick navigation to specific flows based on various criteria.
            </p>
            
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-md">
                <h3 className="font-medium text-blue-900 mb-2">Discovery Features:</h3>
                <ul className="space-y-1 text-sm text-blue-700">
                  <li>• Search by name, description, or elements</li>
                  <li>• Filter by type, status, and grade</li>
                  <li>• Sort by name, date, or score</li>
                  <li>• Navigate from dashboard insights</li>
                </ul>
              </div>
              
              <div className="p-4 bg-purple-50 rounded-md">
                <h3 className="font-medium text-purple-900 mb-2">Management Actions:</h3>
                <ul className="space-y-1 text-sm text-purple-700">
                  <li>• View detailed flow analysis</li>
                  <li>• Export flows to PDF</li>
                  <li>• Sync specific flows</li>
                  <li>• Score flows for quality</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Search Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Powerful Search</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start">
              <MagnifyingGlassIcon className="h-6 w-6 text-blue-600 mt-1" />
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-gray-900">Smart Flow Search</h3>
                <p className="mt-1 text-gray-600">
                  The search bar searches across multiple fields to help you find flows quickly.
                </p>
                
                <div className="mt-4 space-y-3">
                  <div className="p-4 bg-gray-50 rounded-md">
                    <h4 className="font-medium text-gray-900 mb-2">Search Includes:</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li>• <strong>Flow Label:</strong> The display name of the flow</li>
                      <li>• <strong>API Name:</strong> The developer name (e.g., Order_Fulfillment_Process)</li>
                      <li>• <strong>Description:</strong> Any description text added to the flow</li>
                      <li>• <strong>Process Type:</strong> AutoLaunched, Screen, Triggered, etc.</li>
                      <li>• <strong>Elements:</strong> Search for flows containing specific elements</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-md">
                    <h4 className="font-medium text-blue-900 mb-2">💡 Search Examples:</h4>
                    <ul className="space-y-1 text-sm text-blue-700">
                      <li>• "opportunity" - finds all flows related to opportunities</li>
                      <li>• "email" - finds flows with email alerts or email-related logic</li>
                      <li>• "before save" - finds record-triggered flows that run before save</li>
                      <li>• "screen" - finds all screen flows</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Filtering Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Advanced Filtering</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start">
                <FunnelIcon className="h-6 w-6 text-purple-600 mt-1" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Multi-Level Filters</h3>
                  <p className="mt-1 text-gray-600 mb-4">
                    Combine filters to narrow down to exactly the flows you need to work with.
                  </p>
                  
                  <div className="grid gap-4">
                    <div>
                      <h4 className="font-medium text-gray-900">Flow Type</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        All types, Screen Flow, Record-Triggered, Scheduled, Autolaunched, Platform Event, and more
                      </p>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900">Performance Grade</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Filter by quality grades: A (Excellent), B (Good), C (Fair), D (Poor), I (Incomplete), or Unscored
                      </p>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900">Performance Issues</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Filter by specific issues: Missing Description, Complex Flow, Hardcoded IDs, No Error Handling, etc.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900">Package State</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Show all flows, unpackaged only, or from managed packages
                      </p>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900">Active Status</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Toggle to show only active flows or include inactive ones
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sorting Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Sorting Options</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start">
              <AdjustmentsHorizontalIcon className="h-6 w-6 text-green-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Organize Your View</h3>
                <p className="mt-1 text-gray-600 mb-4">
                  Sort flows to prioritize what matters most to you right now.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="font-medium text-gray-700 w-32">Name:</span>
                    <span className="text-sm text-gray-600">Alphabetical by flow label (A-Z or Z-A)</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium text-gray-700 w-32">Last Modified:</span>
                    <span className="text-sm text-gray-600">Most recent changes first or oldest first</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium text-gray-700 w-32">Score:</span>
                    <span className="text-sm text-gray-600">Highest performing flows first or lowest first</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Flow Cards Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Understanding Flow Cards</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Each Flow Card Contains:</h3>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-32 text-sm font-medium text-gray-500">Header:</div>
                <div className="flex-1">
                  <p className="text-gray-700">Flow name with active/inactive status and type badge</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-32 text-sm font-medium text-gray-500">Grade Badge:</div>
                <div className="flex-1">
                  <p className="text-gray-700">Performance grade (A-D, I) with score percentage and color coding</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-32 text-sm font-medium text-gray-500">Description:</div>
                <div className="flex-1">
                  <p className="text-gray-700">Flow description or trigger details for record-triggered flows</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-32 text-sm font-medium text-gray-500">Metadata:</div>
                <div className="flex-1">
                  <p className="text-gray-700">API name, namespace (if packaged), last modified date</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-32 text-sm font-medium text-gray-500">Sync Status:</div>
                <div className="flex-1">
                  <p className="text-gray-700">Shows if flow is processing, has errors, or needs scoring</p>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-green-50 rounded-md">
              <p className="text-sm font-medium text-green-900">🎯 Quick Actions:</p>
              <p className="text-sm text-green-700 mt-1">
                Click any flow card to view detailed analysis including scorecard, element breakdown, and improvement suggestions.
              </p>
            </div>
          </div>
        </section>

        {/* Actions Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Available Actions</h2>
          
          <div className="grid gap-4">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start">
                <ArrowDownTrayIcon className="h-6 w-6 text-blue-600 mt-1" />
                <div className="ml-4">
                  <h3 className="font-medium text-gray-900">Export Flows</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Export selected flows to PDF for documentation, review, or sharing. Includes flow diagram and analysis.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start">
                <ArrowPathIcon className="h-6 w-6 text-blue-600 mt-1" />
                <div className="ml-4">
                  <h3 className="font-medium text-gray-900">Sync Options</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    • <strong>Check Updates:</strong> See what's changed in Salesforce<br/>
                    • <strong>Sync All Flows:</strong> Full refresh of all flow data<br/>
                    • <strong>Refresh Flow:</strong> Update individual flow (via detail page)
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start">
                <ChartBarIcon className="h-6 w-6 text-purple-600 mt-1" />
                <div className="ml-4">
                  <h3 className="font-medium text-gray-900">Score All Flows</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Analyze all unscored flows to calculate performance grades and identify improvement opportunities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Context Navigation */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Smart Navigation</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Context-Aware Filtering</h3>
                <p className="text-gray-600 mb-4">
                  When you navigate from the dashboard, flows are automatically filtered based on what you clicked:
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• <strong>Critical Issues card:</strong> Shows flows with critical problems</li>
                  <li>• <strong>Incomplete Flows card:</strong> Shows flows graded "I"</li>
                  <li>• <strong>Issue Distribution bar:</strong> Shows flows with that specific issue</li>
                  <li>• <strong>Burn-down chart column:</strong> Shows flows modified on that date</li>
                  <li>• <strong>Error type bar:</strong> Shows flows with that error type</li>
                </ul>
                <div className="mt-4 p-3 bg-amber-50 rounded-md">
                  <p className="text-xs text-amber-800">Context messages appear at the top of the page to explain the current filter</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pro Tips */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Pro Tips</h2>
          
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-6 border border-yellow-200">
            <div className="flex items-start">
              <LightBulbIcon className="h-6 w-6 text-yellow-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Workflow Optimization</h3>
                <ol className="space-y-3 text-gray-700">
                  <li>
                    <strong>1. Start with Grades:</strong>
                    <p className="text-sm mt-1">Filter by D and I grades to find flows needing the most attention</p>
                  </li>
                  <li>
                    <strong>2. Use Performance Issues:</strong>
                    <p className="text-sm mt-1">Filter by specific issues like "Hardcoded IDs" to fix similar problems in bulk</p>
                  </li>
                  <li>
                    <strong>3. Regular Scoring:</strong>
                    <p className="text-sm mt-1">Run "Score All Flows" weekly to track improvement progress</p>
                  </li>
                  <li>
                    <strong>4. Export for Reviews:</strong>
                    <p className="text-sm mt-1">Use PDF export for code reviews or documentation</p>
                  </li>
                  <li>
                    <strong>5. Save Filter Settings:</strong>
                    <p className="text-sm mt-1">Your active-only and package filters persist across sessions</p>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* Next Steps */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Next Steps</h2>
          
          <div className="grid gap-4">
            <Link
              to={`${basePath}/flows`}
              className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <BoltIcon className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">Explore Your Flows</h4>
                    <p className="text-sm text-gray-600">Start analyzing and improving your automations</p>
                  </div>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-gray-400" />
              </div>
            </Link>

            <Link
              to={`${basePath}/help/best-practices`}
              className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <LightBulbIcon className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">Flow Best Practices</h4>
                    <p className="text-sm text-gray-600">Learn how to build better flows</p>
                  </div>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-gray-400" />
              </div>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <Link to={`${basePath}/help`} className="text-blue-600 hover:text-blue-800 flex items-center">
              <ArrowRightIcon className="h-4 w-4 mr-1 rotate-180" />
              Back to Help Center
            </Link>
            <Link to={`${basePath}/flows`} className="text-blue-600 hover:text-blue-800 flex items-center">
              View Your Flows
              <ArrowRightIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}